import Quill from 'quill';
import ReactQuill from 'react-quill';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useEffect, useRef } from 'react';
import { PlainClipboard } from './PlainClipboard';
import { HtmlScrubber } from './HtmlScrubber';
import { createUseStyles } from 'react-jss';

// at some point we may want to copy quill.snow.css here, keeping the parts we want
// so we don't have to reason about both style sheets
import 'react-quill/dist/quill.snow.css';

type IStyleKeys = 'quill';
interface IStyleProps { height?: string }

const useStyles = createUseStyles<IStyleKeys, IStyleProps>({
    quill: {
        '& .ql-editor': {
            // passed in by parent component
            height: ({ height }) => height,

            // need room for the selection handle when a word in the top line is selected
            marginTop: 12,

            fontSize: 16,
            fontFamily: 'avenir-next'
        },

        '& .ql-toolbar': {
            border: 'none',
            borderBottom: '1px solid rgb(208, 208, 208)'
        },

        '& .ql-container': {
            border: 'none'
        }
    }
});

Quill.register('modules/clipboard', PlainClipboard, true);

export interface IQuillEditorProps {
    placeholder: string;
    autofocus?: boolean;
    preventBlur?: boolean;
    height?: string;
    html: string;
    onChange: (html: string) => void;
}

export const QuillEditor: React.FC<IQuillEditorProps> = ({ placeholder, autofocus, preventBlur, height, html, onChange }) => {
    const classes = useStyles({ height });

    const scrubber = useInstance(HtmlScrubber);

    const quillRef = useRef<ReactQuill & { apCanBlur?: boolean }>(null);

    useEffect(() => {
        if (autofocus && !html) {
            setTimeout(() => {
                if (quillRef.current) {
                    quillRef.current.focus();
                }
            }, 100); // need to give quill a chance to render before we focus it
        }

        // unmount
        const ref = quillRef;
        return () => {
            if (ref.current) {
                ref.current.apCanBlur = true;
                ref.current.blur();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (quillRef.current) {

            if (preventBlur) {
                // on selection change is called when quill is first focused (and subsequently)
                // so flag that we don't want to allow blur once it has gained focus
                quillRef.current.editor?.on('selection-change', () => {
                    if (quillRef.current) {
                        quillRef.current.apCanBlur = false;
                    }
                });

                // when trying to blur, refocus (don't allow blur) unless it has been flagged to allow
                // (by saving it)
                quillRef.current.editor?.root.addEventListener('focusout', (e: any) => {
                    if (quillRef.current) {
                        if (!quillRef.current.apCanBlur){
                        // need to allow the focusout to finish, then can focus
                        // ideally we'd prevent the blur instead but I wasn't able to get that to work
                            setTimeout(() => {
                                if (quillRef.current) {
                                    quillRef.current.focus();
                                }
                            });
                        }
                    }
                });
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quillRef?.current]);

    const modules = {
        toolbar: [
            // [{ 'header': [1, 2, false] }],
            ['bold', 'italic', 'underline'],
            //,'strike', 'blockquote'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            // [{'indent': '-1'}, {'indent': '+1'}],
            // ['link'], // the UX for link editing isn't good enough on Mobile (https://merakisolutionsinc.slack.com/archives/C02S2G6H3FZ/p1667241659801999?thread_ts=1667241539.052649&cid=C02S2G6H3FZ)
            // ['image'] // embeds an inline image, I didn't test on device, but likely works fine... just need a store for bigger notes payload
            // ['clean'] // "removes the formatting button" ... I couldn't figure out what this does
        ]
    };

    const handleChange = (updatedHtml: string) => {
        const scrubbed = scrubber.scrub(updatedHtml);
        if (scrubbed !== html) {
            onChange(scrubbed);
        }
    };

    return (
        <ReactQuill
            className={classes.quill}
            theme="snow"
            ref={quillRef}
            value={html}
            onChange={handleChange}
            placeholder={placeholder}
            modules={modules}
        />
    );
};
